// src/modulos/Parametros/StockChangeReasonPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const STOCK_REASON_COLLECTION = 'parameters/data/motivo-ajuste-estoque';
const ITEMS_PER_PAGE = 10;

// Tipos de ajuste permitidos
const ADJUSTMENT_TYPES = {
    IN: 'entrada',
    OUT: 'saida',
    BOTH: 'ambos'
};

const ADJUSTMENT_TYPE_LABELS = {
    [ADJUSTMENT_TYPES.IN]: 'Entrada',
    [ADJUSTMENT_TYPES.OUT]: 'Saída',
    [ADJUSTMENT_TYPES.BOTH]: 'Entrada e Saída'
};

const StockChangeReasonPage = () => {
    const can = usePagePermissions('motivo_alteracao_estoque');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [nameInput, setNameInput] = useState('');
    const [typeInput, setTypeInput] = useState(ADJUSTMENT_TYPES.BOTH);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteCheckLoading, setDeleteCheckLoading] = useState(false);
    const [deleteCheckResult, setDeleteCheckResult] = useState(null);

    // Hook de Dados
    const {
        data: reasons,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(STOCK_REASON_COLLECTION, 'name');

    // ============================================================
    // 1. DEFINIR filteredReasons ANTES dos callbacks
    // ============================================================
    const filteredReasons = useMemo(() => {
        if (!reasons) return [];
        if (!searchTerm) return reasons;

        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return reasons.filter(item =>
            item.name.toLowerCase().includes(lowerCaseSearchTerm)
        );
    }, [reasons, searchTerm]);

    const totalFilteredItems = filteredReasons.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const paginatedReasons = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredReasons.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredReasons, currentPage]);

    // Verificar uso do motivo
    const checkReasonInUse = useCallback(async (reasonId) => {
        setDeleteCheckLoading(true);
        setDeleteCheckResult(null);

        try {
            const { db } = await import('../../firebaseConfig');
            const { collection, query, where, getDocs } = await import('firebase/firestore');

            const movementsRef = collection(db, 'stock-movements');
            const q = query(movementsRef, where('reasonId', '==', reasonId));
            const querySnapshot = await getDocs(q);
            const usageCount = querySnapshot.size;

            if (usageCount > 0) {
                setDeleteCheckResult({
                    inUse: true,
                    count: usageCount,
                    message: `Este motivo foi utilizado em ${usageCount} movimentação(ões) de estoque. Ao deletá-lo, o histórico dessas movimentações ficará sem referência.`
                });
            } else {
                setDeleteCheckResult({ inUse: false, count: 0, message: null });
            }
        } catch (error) {
            console.error('Erro ao verificar uso do motivo:', error);
            setDeleteCheckResult({
                inUse: false,
                error: true,
                message: 'Não foi possível verificar se este motivo está em uso. Tente novamente.'
            });
        } finally {
            setDeleteCheckLoading(false);
        }
    }, []);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setNameInput('');
        setTypeInput(ADJUSTMENT_TYPES.BOTH);
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setNameInput(item.name);
        setTypeInput(item.type || ADJUSTMENT_TYPES.BOTH);
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
        setDeleteCheckResult(null);
        checkReasonInUse(id);
    }, [checkReasonInUse]);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;

        if (deleteCheckResult && deleteCheckResult.inUse) {
            setFormError('Não é possível deletar: este motivo já foi utilizado em movimentações de estoque.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
            setTimeout(() => {
                setFormError('');
                setDeleteCheckResult(null);
            }, 3000);
            return;
        }

        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Motivo "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const newTotalItems = totalFilteredItems - 1;
                const maxPage = Math.ceil(newTotalItems / ITEMS_PER_PAGE);
                if (currentPage > maxPage && maxPage > 0) {
                    setCurrentPage(maxPage);
                } else if (newTotalItems === 0) {
                    setCurrentPage(1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Motivo de Ajuste de Estoque: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar motivo. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
            setDeleteCheckResult(null);
        }
    }, [deleteConfirm, deleteCheckResult, deleteDocument, totalFilteredItems, currentPage]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError("O nome do Motivo de Ajuste é obrigatório.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: nameInput.trim(),
            type: typeInput,
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Motivo editado com sucesso!' : 'Motivo adicionado com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, typeInput, editingItem, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    // Renderização do badge de tipo
    const renderTypeBadge = useCallback((type) => {
        let customStyle = {};

        if (type === ADJUSTMENT_TYPES.IN) {
            customStyle = { background: 'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)', color: '#155724' };
        } else if (type === ADJUSTMENT_TYPES.OUT) {
            customStyle = { background: 'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)', color: '#721c24' };
        } else {
            customStyle = { background: 'linear-gradient(135deg, #e2e3e5 0%, #d6d8db 100%)', color: '#383d41' };
        }

        return (
            <span className={styles.badge} style={customStyle}>
                {ADJUSTMENT_TYPE_LABELS[type] || 'Não definido'}
            </span>
        );
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Novo Motivo de Ajuste'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Motivo'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Motivo do Ajuste <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        className={styles.input}
                        placeholder="Ex: Perda, Amostra, Erro de Contagem, Devolução"
                        required
                        disabled={formLoading}
                        autoFocus
                    />
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="type">
                        Tipo de Ajuste <span className={styles.required}>*</span>
                    </label>
                    <select
                        id="type"
                        value={typeInput}
                        onChange={(e) => setTypeInput(e.target.value)}
                        className={styles.select}
                        disabled={formLoading}
                    >
                        <option value={ADJUSTMENT_TYPES.IN}>Entrada (aumenta o estoque)</option>
                        <option value={ADJUSTMENT_TYPES.OUT}>Saída (diminui o estoque)</option>
                        <option value={ADJUSTMENT_TYPES.BOTH}>Ambos (entrada e saída)</option>
                    </select>
                    <small className={styles.fieldHint}>
                        Define se este motivo pode ser usado em ajustes de entrada, saída ou ambos.
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar o motivo "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita.`}
                isLoading={deleteLoading || deleteCheckLoading}
                danger={true}
            >
                {deleteCheckLoading && (
                    <div className={styles.realtimeInfo} style={{ margin: '16px 0' }}>
                        <span className={styles.liveDot}></span>
                        <span>Verificando se o motivo está em uso...</span>
                    </div>
                )}

                {deleteCheckResult && deleteCheckResult.inUse && (
                    <div className={`${styles.alert} ${styles.error}`} style={{ margin: '16px 0' }}>
                        <span className={styles.alertIcon}>⚠️</span>
                        {deleteCheckResult.message}
                    </div>
                )}

                {deleteCheckResult && deleteCheckResult.error && (
                    <div className={`${styles.alert} ${styles.warning}`} style={{ margin: '16px 0' }}>
                        <span className={styles.alertIcon}>⚠️</span>
                        {deleteCheckResult.message}
                    </div>
                )}

                {deleteCheckResult && !deleteCheckResult.inUse && !deleteCheckResult.error && !deleteCheckLoading && (
                    <div className={`${styles.alert} ${styles.success}`} style={{ margin: '16px 0' }}>
                        <span className={styles.alertIcon}>✓</span>
                        Este motivo não foi utilizado em nenhuma movimentação de estoque.
                    </div>
                )}
            </ConfirmModal>

            {/* Page Header */}
            <PageHeader
                title="Motivos de Ajuste de Estoque"
                actionText="Adicionar Motivo"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar motivo..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={3} rows={ITEMS_PER_PAGE} />
                    ) : paginatedReasons.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📦'}
                            title={
                                searchTerm
                                    ? 'Nenhum motivo encontrado'
                                    : 'Nenhum motivo de ajuste cadastrado'
                            }
                            message={
                                searchTerm
                                    ? `Nenhum resultado para "${searchTerm}"`
                                    : 'Clique em "Adicionar Motivo" para começar'
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Motivo de Ajuste</th>
                                            <th>Tipo</th>
                                            <th style={{ width: '150px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedReasons.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Alterado: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.daysBadge}>
                                                    {renderTypeBadge(item.type || ADJUSTMENT_TYPES.BOTH)}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading || deleteCheckLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="motivos"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default StockChangeReasonPage;